import React, { useState, useRef, useEffect } from 'react';
import ReactDOM from 'react-dom';
import styled from 'styled-components';
import PropTypes from 'prop-types';
import { HAMBURGER_MENU_Z_INDEX } from 'web-ui-kit/zIndexes';
import { surfaceM, primaryM, textPrimaryM, museumGrey, grayLightM, white } from 'web-ui-kit/colors';
import { drawerOverlayClicked, navCtaHovered, navCtaClicked, navItemHovered, navItemClicked, secondaryListItemClicked, listItemSublevelClicked, hamburgerToggleClicked, dropdownMenuSmallClicked, navItemWithChevronSmallClicked } from 'web-ui-kit/events';
import ModsyLogo from 'web-ui-kit/assets/ModsyLogo';
import { DropdownMenuLarge, DropdownMenuSmall, Accordion, SecondaryList } from 'web-ui-kit/menus';
import { webPP, allRoutes } from 'web-ui-kit/routes';
import { ButtonLabelStyles, BodySmallBoldLargeStyles, BodyBoldSmallStyles } from 'web-ui-kit/typography';
import get from 'lodash/get';
import HelpIcon from 'web-ui-kit/assets/HelpIcon';
import Arrow from 'web-ui-kit/assets/Arrow';
import HamburgerToggle from 'web-ui-kit/assets/HamburgerToggle';
import NavigationModal from 'web-ui-kit/modals/NavigationModal';
import { MQ_FOR_PHONE_WIDTH, MQ_FOR_TABLET_LANDSCAPE_UP, MQ_FOR_TABLET_PORTRAIT_UP_NO_MIN_HEIGHT, MQ_FOR_SMALL_TABLET_ONLY, MQ_FOR_TABLET_LANDSCAPE_BELOW } from 'web-ui-kit/media';
import ModsyHeart from 'web-ui-kit/assets/ModsyHeart';
import { DESIGN_IDEAS_DRAWER_LABELS, setDropdownTwoLineList, HELP_MODAL_PROPS, HEADER_NAMES, setSublevelTwoLineList, setSublevelOneLineList, setSecondaryListItemList, LOGGED_OUT_LABELS, ACCOUNT_DRAWER_LABELS, DROPDOWN_DESIGN_IDEAS_LIST, DROPDOWN_ACCOUNT_LIST_WITH_HELP, DROPDOWN_HELP_LIST, DROPDOWN_HOW_IT_WORKS_LIST } from 'web-ui-kit/dataHeader';
import AccountIcon from 'web-ui-kit/assets/AccountIcon';

function _extends() {
  _extends = Object.assign || function (target) {
    for (var i = 1; i < arguments.length; i++) {
      var source = arguments[i];

      for (var key in source) {
        if (Object.prototype.hasOwnProperty.call(source, key)) {
          target[key] = source[key];
        }
      }
    }

    return target;
  };

  return _extends.apply(this, arguments);
}

function _taggedTemplateLiteral(strings, raw) {
  if (!raw) {
    raw = strings.slice(0);
  }

  return Object.freeze(Object.defineProperties(strings, {
    raw: {
      value: Object.freeze(raw)
    }
  }));
}

function _slicedToArray(arr, i) {
  return _arrayWithHoles(arr) || _iterableToArrayLimit(arr, i) || _unsupportedIterableToArray(arr, i) || _nonIterableRest();
}

function _arrayWithHoles(arr) {
  if (Array.isArray(arr)) return arr;
}

function _iterableToArrayLimit(arr, i) {
  if (typeof Symbol === "undefined" || !(Symbol.iterator in Object(arr))) return;
  var _arr = [];
  var _n = true;
  var _d = false;
  var _e = undefined;

  try {
    for (var _i = arr[Symbol.iterator](), _s; !(_n = (_s = _i.next()).done); _n = true) {
      _arr.push(_s.value);

      if (i && _arr.length === i) break;
    }
  } catch (err) {
    _d = true;
    _e = err;
  } finally {
    try {
      if (!_n && _i["return"] != null) _i["return"]();
    } finally {
      if (_d) throw _e;
    }
  }

  return _arr;
}

function _unsupportedIterableToArray(o, minLen) {
  if (!o) return;
  if (typeof o === "string") return _arrayLikeToArray(o, minLen);
  var n = Object.prototype.toString.call(o).slice(8, -1);
  if (n === "Object" && o.constructor) n = o.constructor.name;
  if (n === "Map" || n === "Set") return Array.from(n);
  if (n === "Arguments" || /^(?:Ui|I)nt(?:8|16|32)(?:Clamped)?Array$/.test(n)) return _arrayLikeToArray(o, minLen);
}

function _arrayLikeToArray(arr, len) {
  if (len == null || len > arr.length) len = arr.length;

  for (var i = 0, arr2 = new Array(len); i < len; i++) {
    arr2[i] = arr[i];
  }

  return arr2;
}

function _nonIterableRest() {
  throw new TypeError("Invalid attempt to destructure non-iterable instance.\nIn order to be iterable, non-array objects must have a [Symbol.iterator]() method.");
}

var _templateObject, _templateObject2;

function Drawer(_ref) {
  var children = _ref.children,
      _ref$containerElement = _ref.containerElementId,
      containerElementId = _ref$containerElement === void 0 ? '' : _ref$containerElement,
      dispatch = _ref.dispatch,
      _ref$isDrawerOpen = _ref.isDrawerOpen,
      isDrawerOpen = _ref$isDrawerOpen === void 0 ? false : _ref$isDrawerOpen,
      _ref$isLoggedOutShare = _ref.isLoggedOutSharedMode,
      isLoggedOutSharedMode = _ref$isLoggedOutShare === void 0 ? false : _ref$isLoggedOutShare,
      menuId = _ref.menuId,
      _ref$topPosition = _ref.topPosition,
      topPosition = _ref$topPosition === void 0 ? '3rem' : _ref$topPosition; // Allows us to animate the modal in and out.
  // When we toggle between the 'before-animation' and
  // 'after-animation' states it will trigger the CSS animation.

  var _useState = useState('before-animation'),
      _useState2 = _slicedToArray(_useState, 2),
      animateState = _useState2[0],
      setAnimateState = _useState2[1]; // When the drawer mounts, lock body scroll.
  // When the drawer unmounts, unlock body scroll and restore original styles.


  var pageYOffset = useRef();
  var overflow = useRef();
  var position = useRef();
  useEffect(function () {
    if (isDrawerOpen) {
      setAnimateState('after-animation'); // When the drawer mounts, lock body scroll.

      if (typeof window !== 'undefined' && typeof document !== 'undefined') {
        overflow.current = document.body.style.overflow;
        pageYOffset.current = window.pageYOffset;
        position.current = document.body.style.position; // This locks body scroll on everything except iOS.

        document.body.style.overflow = 'hidden'; // This is required on iOS to lock body scroll.
        // It also resets scrollY to zero which is why
        // we need also need to save scrollY on mount and
        // then restore it on unmount

        document.body.style.position = 'fixed';
      }
    } else {
      setAnimateState('before-animation');
    } // When the drawer unmounts, unlock body scroll and restore original styles.


    return function () {
      if (typeof window !== 'undefined' && typeof document !== 'undefined') {
        document.body.style.overflow = overflow.current;
        document.body.style.position = position.current;
        window.scrollTo(0, pageYOffset.current);
      }
    };
  }, [setAnimateState, isDrawerOpen]);

  var handleContainerClick = function handleContainerClick(e) {
    e.preventDefault();
    dispatch({
      type: drawerOverlayClicked
    });
  };

  var handleMenuClick = function handleMenuClick(e) {
    e.stopPropagation();
  };

  if (!isDrawerOpen) {
    return null;
  }

  var appendToElement = containerElementId && document.getElementById(containerElementId) ? document.getElementById(containerElementId) : document.body;
  return (
    /*#__PURE__*/
    ReactDOM.createPortal(
    /*#__PURE__*/
    React.createElement(Container, {
      animateState: animateState,
      "aria-expanded": isDrawerOpen,
      "aria-label": "drawer-menu-overlay",
      isOpen: isDrawerOpen,
      onClick: handleContainerClick,
      topPosition: topPosition
    },
    /*#__PURE__*/
    React.createElement(Menu, {
      animateState: animateState,
      "aria-expanded": isDrawerOpen,
      "aria-label": "drawer-menu",
      id: menuId,
      isOpen: isDrawerOpen,
      onClick: handleMenuClick,
      paddingSpacer: topPosition
    }, React.Children.map(children, function (child) {
      if (!child) {
        return null;
      }

      return (
        /*#__PURE__*/
        React.cloneElement(child, {
          dispatch: dispatch,
          isLoggedOutSharedMode: isLoggedOutSharedMode
        })
      );
    }))), appendToElement)
  );
}

Drawer.propTypes = {
  /** used for bubbling events */
  dispatch: PropTypes.func,

  /** indicates state of popup menu */
  isOpen: PropTypes.bool,

  /** children are rendered as menu contents */
  children: PropTypes.node.isRequired,

  /** id used for aria-controls on toggle */
  menuId: PropTypes.string.isRequired,

  /** sets top position of fixed container */
  topPosition: PropTypes.string
};
var Container = styled.div(_templateObject || (_templateObject = _taggedTemplateLiteral(["\n  position: fixed;\n  // top: calc(", ");\n  // bottom: 0;\n  left: 0;\n  right: 0;\n  z-index: ", ";\n  cursor: pointer;\n  top: auto;\n  bottom: auto;\n  height: calc(100vh - ", ");\n  overflow: scroll;\n\n  /* Fade the modal overlay in and out */\n  background-color: rgba(0, 0, 0, 0);\n  transition: background-color 0.4s ease-in-out;\n  ", "\n"])), function (props) {
  return props.topPosition;
}, HAMBURGER_MENU_Z_INDEX, function (props) {
  return props.topPosition;
}, function (props) {
  return props.animateState === 'after-animation' && "\n    background-color: rgba(0, 0, 0, 0.75);\n  ";
});
var Menu = styled.nav(_templateObject2 || (_templateObject2 = _taggedTemplateLiteral(["\n  background: ", ";\n  position: absolute;\n  transition: transform 0.5s ease;\n  padding-bottom: ", ";\n  right: 0;\n  top: 0;\n  left: 2.5rem;\n  bottom: 0;\n  z-index: 1;\n  height: 100vh;\n  overflow-y: auto;\n  -webkit-overflow-scrolling: touch;\n  cursor: auto;\n\n  /* Slide the modal content in/out from the left. */\n  transform: translateX(100%);\n  transition: transform 0.4s ease-in-out;\n  ", "\n"])), surfaceM, function (props) {
  return props.paddingSpacer;
}, function (props) {
  return props.animateState === 'after-animation' && "\n    transform: translateX(0);\n  ";
});

var _templateObject$1, _templateObject2$1;

function NavCta(_ref) {
  var _ref$dispatch = _ref.dispatch,
      dispatch = _ref$dispatch === void 0 ? function () {} : _ref$dispatch,
      _ref$label = _ref.label,
      label = _ref$label === void 0 ? '' : _ref$label,
      _ref$variant = _ref.variant,
      variant = _ref$variant === void 0 ? 'default' : _ref$variant,
      _ref$navigateTo = _ref.navigateTo,
      navigateTo = _ref$navigateTo === void 0 ? '' : _ref$navigateTo;
  return (
    /*#__PURE__*/
    React.createElement(Container$1, {
      variant: variant,
      onMouseEnter: function onMouseEnter() {
        return dispatch({
          type: navCtaHovered,
          payload: {
            label: label
          }
        });
      },
      onClick: function onClick(event) {
        return dispatch({
          type: navCtaClicked,
          payload: {
            href: navigateTo,
            event: event,
            text: label
          }
        });
      }
    },
    /*#__PURE__*/
    React.createElement(Link, {
      variant: variant,
      href: navigateTo
    }, label))
  );
}

NavCta.propTypes = {
  /** mechanism for bubbling events up */
  dispatch: PropTypes.func.isRequired,

  /** user facing copy */
  label: PropTypes.string.isRequired,

  /** one of: default, small */
  variant: PropTypes.string,

  /** destination to redirect user to on click */
  navigateTo: PropTypes.string.isRequired
}; // Styles

var Container$1 = styled.button(_templateObject$1 || (_templateObject$1 = _taggedTemplateLiteral(["\n  height: 2.5rem;\n  width: fit-content;\n\n  ", "\n\n  background-color: ", ";\n  border: none;\n  border-radius: 0.25rem;\n  cursor: pointer;\n  padding-left: 1rem;\n  padding-right: 1rem;\n  margin-left: 0.5rem;\n  transition: all ease 0.2s;\n\n  display: flex;\n  justify-content: center;\n  align-items: center;\n\n  &:hover {\n    background-color: ", ";\n  }\n\n  > a {\n    color: ", ";\n  }\n"])), function (p) {
  return p.variant === 'small' && "\n    height: 2.125rem;\n  ";
}, primaryM, primaryM, surfaceM);
var Link = styled.a(_templateObject2$1 || (_templateObject2$1 = _taggedTemplateLiteral(["\n  ", "\n\n  /** Link needs to fill up the entire cell so that it\n  updates color on hover of the container and the entire\n  cell is clickable */\n  display: flex;\n  align-items: center;\n  height: 100%;\n  text-decoration: none;\n\n  ", "\n"])), ButtonLabelStyles, function (p) {
  return p.variant === 'small' && "\n    font-size: 0.875rem;\n  ";
});

var _templateObject$2, _templateObject2$2;

function NavItemLarge(_ref) {
  var dispatch = _ref.dispatch,
      label = _ref.label,
      navigateTo = _ref.navigateTo;
  return (
    /*#__PURE__*/
    React.createElement(Container$2, {
      onMouseEnter: function onMouseEnter() {
        return dispatch({
          type: navItemHovered,
          payload: {
            label: label
          }
        });
      },
      onClick: function onClick(event) {
        return dispatch({
          type: navItemClicked,
          payload: {
            href: navigateTo,
            event: event,
            isExternalLink: true,
            text: label
          }
        });
      }
    },
    /*#__PURE__*/
    React.createElement(Link$1, {
      href: navigateTo
    }, label))
  );
}

NavItemLarge.propTypes = {
  /** mechanism for bubbling events up */
  dispatch: PropTypes.func.isRequired,

  /** user facing copy */
  label: PropTypes.string.isRequired,

  /** destination to redirect user to on click */
  navigateTo: PropTypes.string.isRequired
}; // Styles

var Container$2 = styled.div(_templateObject$2 || (_templateObject$2 = _taggedTemplateLiteral(["\n  height: calc(4rem - 1px);\n  width: fit-content;\n\n  background-color: ", ";\n  border: none;\n  cursor: pointer;\n  transition: all ease 0.2s;\n\n  display: flex;\n  justify-content: center;\n  align-items: center;\n\n  &:hover {\n    color: ", ";\n    background-color: transparent;\n  }\n\n  > a {\n    color: ", ";\n    &:hover {\n      color: ", ";\n      background-color: transparent;\n    }\n  }\n"])), surfaceM, primaryM, textPrimaryM, primaryM);
var Link$1 = styled.a(_templateObject2$2 || (_templateObject2$2 = _taggedTemplateLiteral(["\n  padding-left: 0.75rem;\n  padding-right: 0.75rem;\n\n  ", ";\n  color: ", ";\n\n  /** Link needs to fill up the entire cell so that it\n  turns blue on hover of the container and the entire\n  cell is clickable */\n  display: flex;\n  align-items: center;\n  height: 100%;\n  width: 100%;\n  text-decoration: none;\n"])), BodySmallBoldLargeStyles, primaryM);

var _templateObject$3, _templateObject2$3, _templateObject3, _templateObject4, _templateObject5;

function HeaderLarge(_ref) {
  var _ref$className = _ref.className,
      className = _ref$className === void 0 ? '' : _ref$className,
      _ref$dispatch = _ref.dispatch,
      dispatch = _ref$dispatch === void 0 ? function () {} : _ref$dispatch,
      _ref$isLoggedOutShare = _ref.isLoggedOutSharedMode,
      isLoggedOutSharedMode = _ref$isLoggedOutShare === void 0 ? false : _ref$isLoggedOutShare,
      _ref$icons = _ref.icons,
      icons = _ref$icons === void 0 ? {} : _ref$icons,
      _ref$items = _ref.items,
      items = _ref$items === void 0 ? {} : _ref$items,
      _ref$labels = _ref.labels,
      labels = _ref$labels === void 0 ? {} : _ref$labels,
      _ref$linkComponent = _ref.linkComponent,
      linkComponent = _ref$linkComponent === void 0 ? function (_ref2) {
    var children = _ref2.children;
    return (
      /*#__PURE__*/
      React.createElement("button", null, children)
    );
  } : _ref$linkComponent,
      _ref$modsyButtonConfi = _ref.modsyButtonConfig,
      modsyButtonConfig = _ref$modsyButtonConfi === void 0 ? {
    destination: '/',
    app: 'web-post-purchase'
  } : _ref$modsyButtonConfi,
      _ref$onCartClick = _ref.onCartClick,
      _ref$showHelpMenu = _ref.showHelpMenu,
      showHelpMenu = _ref$showHelpMenu === void 0 ? false : _ref$showHelpMenu;
  return (
    /*#__PURE__*/
    React.createElement(Container$3, null,
    /*#__PURE__*/
    React.createElement(NavPrimary, null,
    /*#__PURE__*/
    React.createElement(StyledModsyLogo, {
      dispatch: dispatch,
      linkComponent: linkComponent,
      modsyButtonConfig: modsyButtonConfig
    }),
    /*#__PURE__*/
    React.createElement(VerticalLine, {
      role: "img",
      "aria-label": "separator"
    }),
    /*#__PURE__*/
    React.createElement(DropdownMenuLarge, {
      className: className,
      dispatch: dispatch,
      label: labels.myProjects,
      items: items.myProjects,
      linkComponent: linkComponent
    }),
    /*#__PURE__*/
    React.createElement(DropdownMenuLarge, {
      className: className,
      dispatch: dispatch,
      label: labels.designIdeas,
      items: items.designIdeas,
      linkComponent: linkComponent
    })),
    /*#__PURE__*/
    React.createElement(NavSecondary, null, !isLoggedOutSharedMode && showHelpMenu &&
    /*#__PURE__*/
    React.createElement(DropdownMenuLarge, {
      className: className,
      dispatch: dispatch,
      label: labels.help,
      Icon: icons.help,
      items: items.help,
      linkComponent: linkComponent,
      showHelpMenu: showHelpMenu
    }), isLoggedOutSharedMode ?
    /*#__PURE__*/
    React.createElement(React.Fragment, null,
    /*#__PURE__*/
    React.createElement(NavItemLarge, {
      dispatch: dispatch,
      label: labels.login,
      navigateTo: webPP.login()
    }),
    /*#__PURE__*/
    React.createElement(NavCta, {
      label: "Learn more",
      navigateTo: '/',
      dispatch: dispatch
    })) :
    /*#__PURE__*/
    React.createElement(DropdownMenuLarge, {
      className: className,
      dispatch: dispatch,
      label: labels.account,
      Icon: icons.account,
      items: items.account,
      linkComponent: linkComponent,
      showHelpMenu: showHelpMenu
    })))
  );
}

HeaderLarge.propTypes = {
  /** to allow extended styling */
  className: PropTypes.string,

  /** mechanism for bubbling events up */
  dispatch: PropTypes.func.isRequired,

  /** to distinguish logged out users viewing shared studios */
  isLoggedOutSharedMode: PropTypes.bool,

  /** list of items */
  items: PropTypes.object.isRequired,

  /** user facing copies */
  labels: PropTypes.object.isRequired,

  /** number of items currently in cart */
  itemsInCart: PropTypes.number,

  /** to handle routing */
  linkComponent: PropTypes.func,

  /** to handle routing when click on modsy logo */
  modsyButtonConfig: PropTypes.object,

  /** callback fired after click on cart icon */
  onCartClick: PropTypes.func.isRequired,

  /** feature flag to show help menu */
  showHelpMenu: PropTypes.bool
};
var Container$3 = styled.nav(_templateObject$3 || (_templateObject$3 = _taggedTemplateLiteral(["\n  display: flex;\n  justify-content: space-between;\n  align-items: center;\n\n  padding: 1.5rem;\n  height: 4rem;\n  background-color: ", ";\n  border-bottom: 1px solid ", ";\n"])), surfaceM, museumGrey);
var VerticalLine = styled.span(_templateObject2$3 || (_templateObject2$3 = _taggedTemplateLiteral(["\n  border-right: 0.0625rem solid ", ";\n  height: 1.5rem;\n  margin: 0 0.75rem 0 1.5rem;\n"])), grayLightM);
var NavPrimary = styled.div(_templateObject3 || (_templateObject3 = _taggedTemplateLiteral(["\n  display: flex;\n  flex-direction: row;\n  align-items: center;\n"])));
var NavSecondary = styled(NavPrimary)(_templateObject4 || (_templateObject4 = _taggedTemplateLiteral([""])));
var StyledModsyLogo = styled(ModsyLogo)(_templateObject5 || (_templateObject5 = _taggedTemplateLiteral(["\n  height: 3.75rem;\n"])));
var DRAWER_MENU_ID = 'mobile-drawer';

var _templateObject$4, _templateObject2$4, _templateObject3$1, _templateObject4$1, _templateObject5$1;

var APPS = ['', 'nextjs', 'web-post-purchase', 'wordpress'];

function HeaderMedium(_ref) {
  var _ref$backButton = _ref.backButton,
      backButton = _ref$backButton === void 0 ? {
    enabled: false,
    text: 'Back',
    handleClick: function handleClick() {}
  } : _ref$backButton,
      _ref$children = _ref.children,
      children = _ref$children === void 0 ? [] : _ref$children,
      _ref$currentApp = _ref.currentApp,
      currentApp = _ref$currentApp === void 0 ? '' : _ref$currentApp,
      dispatch = _ref.dispatch,
      _ref$helpModalOpen = _ref.helpModalOpen,
      helpModalOpen = _ref$helpModalOpen === void 0 ? false : _ref$helpModalOpen,
      _ref$isLoggedOutShare = _ref.isLoggedOutSharedMode,
      isLoggedOutSharedMode = _ref$isLoggedOutShare === void 0 ? false : _ref$isLoggedOutShare,
      items = _ref.items,
      labels = _ref.labels,
      _ref$linkComponent = _ref.linkComponent,
      linkComponent = _ref$linkComponent === void 0 ? function (_ref2) {
    var children = _ref2.children;
    return (
      /*#__PURE__*/
      React.createElement("button", null, children)
    );
  } : _ref$linkComponent,
      _ref$modsyButtonConfi = _ref.modsyButtonConfig,
      modsyButtonConfig = _ref$modsyButtonConfi === void 0 ? {
    destination: '/',
    app: 'web-post-purchase'
  } : _ref$modsyButtonConfi,
      onCartClick = _ref.onCartClick,
      _ref$onHelpClick = _ref.onHelpClick,
      onHelpClick = _ref$onHelpClick === void 0 ? function () {} : _ref$onHelpClick,
      _ref$showHelpMenu = _ref.showHelpMenu,
      showHelpMenu = _ref$showHelpMenu === void 0 ? false : _ref$showHelpMenu;

  var _useState = useState(false),
      _useState2 = _slicedToArray(_useState, 2),
      isDrawerOpen = _useState2[0],
      setIsDrawerOpen = _useState2[1];

  var _useState3 = useState(false),
      _useState4 = _slicedToArray(_useState3, 2),
      showMyProjects = _useState4[0],
      setMyProjects = _useState4[1];

  var _useState5 = useState(false),
      _useState6 = _slicedToArray(_useState5, 2),
      showDesignIdeas = _useState6[0],
      setDesignIdeas = _useState6[1];

  var _useState7 = useState(false),
      _useState8 = _slicedToArray(_useState7, 2),
      showBlog = _useState8[0],
      setBlog = _useState8[1];

  var handleDrawerDispatch = function handleDrawerDispatch(_ref3) {
    var type = _ref3.type,
        payload = _ref3.payload;
    var isClientSideRoute = get(payload, 'route.app', '') === currentApp;

    switch (true) {
      case type === drawerOverlayClicked:
        setIsDrawerOpen(false);
        break;

      case type === hamburgerToggleClicked:
        setIsDrawerOpen(!isDrawerOpen);
        break;

      case type === listItemSublevelClicked && isClientSideRoute:
      case type === secondaryListItemClicked && isClientSideRoute:
        setIsDrawerOpen(false);
        break;
    }

    dispatch({
      type: type,
      payload: payload
    });
  };

  var handleCloseDropdown = function handleCloseDropdown(dropdownLabel) {
    switch (true) {
      case dropdownLabel === labels.myProjects:
        setMyProjects(false);
        break;

      case dropdownLabel === labels.blog:
        setBlog(false);
        break;

      case dropdownLabel === labels.designIdeas:
        setDesignIdeas(false);
        break;
    }
  };

  var handleDropdownDispatch = function handleDropdownDispatch(event) {
    if (event.type !== dropdownMenuSmallClicked) {
      dispatch(event);
      return;
    } // handle dropdown tap


    var _event$payload = event.payload,
        label = _event$payload.label,
        e = _event$payload.e;
    e.preventDefault();

    switch (true) {
      case label === labels.myProjects:
        setMyProjects(!showMyProjects);
        break;

      case label === labels.designIdeas:
        setDesignIdeas(!showDesignIdeas);
        break;

      case label === labels.blog:
        setBlog(!showBlog);
        break;
    }

    dispatch(event);
  };

  return (
    /*#__PURE__*/
    React.createElement(Container$4, null, !isDrawerOpen &&
    /*#__PURE__*/
    React.createElement(NavPrimary$1, null, backButton.enabled ?
    /*#__PURE__*/
    React.createElement(BackButton, {
      "data-testid": 'back-button',
      onClick: backButton.handleClick
    },
    /*#__PURE__*/
    React.createElement(Arrow, {
      ariaLabel: "back",
      direction: "left"
    })) :
    /*#__PURE__*/
    React.createElement(ModsyLogo, {
      margin: "0 0.5rem",
      variant: "small",
      dispatch: dispatch,
      linkComponent: linkComponent,
      modsyButtonConfig: modsyButtonConfig
    }), !backButton.enabled &&
    /*#__PURE__*/
    React.createElement(React.Fragment, null,
    /*#__PURE__*/
    React.createElement(DropdownMenuSmall, {
      dispatch: handleDropdownDispatch,
      items: items.myProjects,
      label: labels.myProjects,
      showDropdown: showMyProjects,
      linkComponent: linkComponent,
      handleCloseDropdown: handleCloseDropdown,
      variant: "tablet"
    }),
    /*#__PURE__*/
    React.createElement(DropdownMenuSmall, {
      dispatch: handleDropdownDispatch,
      items: items.designIdeas,
      label: labels.designIdeas,
      showDropdown: showDesignIdeas,
      linkComponent: linkComponent,
      handleCloseDropdown: handleCloseDropdown,
      variant: "tablet"
    }))), React.Children.map(children, function (child) {
      if (child.type.name !== Drawer.name && isDrawerOpen) {
        return null;
      }

      return (
        /*#__PURE__*/
        React.cloneElement(child, {
          dispatch: handleDrawerDispatch,
          isDrawerOpen: isDrawerOpen
        })
      );
    }), showHelpMenu && helpModalOpen &&
    /*#__PURE__*/
    React.createElement(StyledNavigationModal, _extends({
      dispatch: dispatch,
      linkComponent: linkComponent
    }, items.help)),
    /*#__PURE__*/
    React.createElement(NavSecondary$1, null, !isDrawerOpen && !isLoggedOutSharedMode && showHelpMenu &&
    /*#__PURE__*/
    React.createElement(HelpIcon, {
      onClick: onHelpClick,
      style: {
        marginRight: '1rem'
      }
    }),
    /*#__PURE__*/
    React.createElement(HamburgerToggle, {
      menuId: DRAWER_MENU_ID,
      isOpen: isDrawerOpen,
      onClick: function onClick() {
        handleDrawerDispatch({
          type: hamburgerToggleClicked,
          payload: {
            isDrawerOpen: !isDrawerOpen
          }
        });
      }
    })))
  );
}

HeaderMedium.propTypes = {
  /** to replace the Modsy logo with a Back button
   * when the PDP is open */
  backButton: PropTypes.object,

  /** allows rendering of children */
  children: PropTypes.node,

  /** indicates from which app the header originates from */
  currentApp: PropTypes.oneOf(APPS),

  /** mechanism for bubbling events up */
  dispatch: PropTypes.func.isRequired,

  /** whether the help modal menu should be displayed */
  helpModalOpen: PropTypes.bool,

  /** to distinguish logged out users viewing shared studios */
  isLoggedOutSharedMode: PropTypes.bool,

  /** list of items in dropdowns and in cart */
  items: PropTypes.object.isRequired,

  /** user facing copies */
  labels: PropTypes.object.isRequired,

  /** to handle routing */
  linkComponent: PropTypes.func,

  /** to handle routing when click on modsy logo */
  modsyButtonConfig: PropTypes.object,

  /** callback fired after click on cart icon */
  onCartClick: PropTypes.func.isRequired,

  /** feature flag to optionally show help menu */
  showHelpMenu: PropTypes.bool
};
var Container$4 = styled.nav(_templateObject$4 || (_templateObject$4 = _taggedTemplateLiteral(["\n  display: flex;\n  justify-content: space-between;\n  align-items: center;\n\n  padding: 1rem 0.5rem;\n  height: 3rem;\n  background-color: ", ";\n  border-bottom: 1px solid ", ";\n"])), surfaceM, museumGrey);
var NavPrimary$1 = styled.div(_templateObject2$4 || (_templateObject2$4 = _taggedTemplateLiteral(["\n  display: flex;\n  flex-direction: row;\n  align-items: center;\n"])));
var NavSecondary$1 = styled(NavPrimary$1)(_templateObject3$1 || (_templateObject3$1 = _taggedTemplateLiteral(["\n  margin-left: auto;\n"])));
var BackButton = styled.div(_templateObject4$1 || (_templateObject4$1 = _taggedTemplateLiteral(["\n  display: flex;\n  align-items: center;\n\n  cursor: pointer;\n  padding: 1.0625rem 0.5rem;\n"])));
var StyledNavigationModal = styled(NavigationModal)(_templateObject5$1 || (_templateObject5$1 = _taggedTemplateLiteral(["\n  ", " {\n    display: none\n  }\n\n  ", " {\n    display: none\n  }\n"])), MQ_FOR_PHONE_WIDTH, MQ_FOR_TABLET_LANDSCAPE_UP);

var _templateObject$5, _templateObject2$5;

var VARIANTS = ['tablet', ''];

function NavItemSmall(_ref) {
  var _ref$className = _ref.className,
      className = _ref$className === void 0 ? '' : _ref$className,
      dispatch = _ref.dispatch,
      label = _ref.label,
      navigateTo = _ref.navigateTo,
      _ref$primaryColor = _ref.primaryColor,
      primaryColor = _ref$primaryColor === void 0 ? false : _ref$primaryColor,
      _ref$smallHeight = _ref.smallHeight,
      smallHeight = _ref$smallHeight === void 0 ? false : _ref$smallHeight,
      _ref$variant = _ref.variant,
      variant = _ref$variant === void 0 ? '' : _ref$variant;
  return (
    /*#__PURE__*/
    React.createElement(Container$5, {
      primaryColor: primaryColor,
      smallHeight: smallHeight,
      className: className,
      onClick: function onClick(event) {
        return dispatch({
          type: navItemClicked,
          payload: {
            href: navigateTo,
            event: event,
            isExternalLink: true,
            text: label
          }
        });
      }
    },
    /*#__PURE__*/
    React.createElement(Link$2, {
      href: navigateTo,
      variant: variant,
      primaryColor: primaryColor
    }, label))
  );
}

NavItemSmall.propTypes = {
  /** Allows extending styles with styled(NavItemSmall) constructor */
  className: PropTypes.string,

  /** mechanism for bubbling events up */
  dispatch: PropTypes.func.isRequired,

  /** user facing copy */
  label: PropTypes.string.isRequired,

  /** destination to redirect user to on click */
  navigateTo: PropTypes.string.isRequired,

  /** variant for styling */
  variant: PropTypes.oneOf(VARIANTS)
}; // Styles

var STYLE_VARIANTS = {
  'tablet': {
    paddingLeft: '0.75rem',
    paddingRight: '0.75rem'
  }
};
var Container$5 = styled.div(_templateObject$5 || (_templateObject$5 = _taggedTemplateLiteral(["\n  height: 3.5rem;\n  width: fit-content;\n\n  background-color: ", ";\n  border: none;\n  cursor: pointer;\n  transition: all ease 0.2s;\n\n  display: flex;\n  justify-content: space-between;\n  align-items: center;\n\n  > a {\n    color: ", ";\n\n    &:active {\n      color: ", ";\n    }\n  }\n\n  ", "\n\n  ", "\n"])), surfaceM, textPrimaryM, primaryM, function (p) {
  return p.primaryColor && "\n    width: 100%;\n    background-color: ".concat(primaryM, ";\n  ");
}, function (p) {
  return p.smallHeight && "\n    height: calc(3rem - 1px);\n  ";
});
var Link$2 = styled.a(_templateObject2$5 || (_templateObject2$5 = _taggedTemplateLiteral(["\n  ", ";\n\n  ", "\n\n  /** Link needs to fill up the entire cell so that it\n  turns blue on hover of the container and the entire\n  cell is clickable */\n  display: flex;\n  align-items: center;\n  height: 100%;\n  text-decoration: none;\n  padding-left: 1rem;\n  padding-right: 1rem;\n  ", ";\n  ", ";\n"])), BodyBoldSmallStyles, function (p) {
  return p.primaryColor && "\n    color: ".concat(white, " !important;\n\n    &:active {\n      background-color: ").concat(primaryM, ";\n    }\n  ");
}, function (props) {
  return props.variant ? "padding-left: ".concat(STYLE_VARIANTS[props.variant]['paddingLeft']) : '';
}, function (props) {
  return props.variant ? "padding-right: ".concat(STYLE_VARIANTS[props.variant]['paddingRight']) : '';
});

var _templateObject$6, _templateObject2$6, _templateObject3$2, _templateObject4$2, _templateObject5$2, _templateObject6;

var APPS$1 = ['', 'nextjs', 'web-post-purchase', 'wordpress'];

function HeaderSmall(_ref) {
  var _ref$backButton = _ref.backButton,
      backButton = _ref$backButton === void 0 ? {
    enabled: false,
    text: 'Back',
    handleClick: function handleClick() {}
  } : _ref$backButton,
      _ref$children = _ref.children,
      children = _ref$children === void 0 ? [] : _ref$children,
      _ref$currentApp = _ref.currentApp,
      currentApp = _ref$currentApp === void 0 ? '' : _ref$currentApp,
      dispatch = _ref.dispatch,
      _ref$helpModalOpen = _ref.helpModalOpen,
      helpModalOpen = _ref$helpModalOpen === void 0 ? false : _ref$helpModalOpen,
      _ref$isLoggedOutShare = _ref.isLoggedOutSharedMode,
      isLoggedOutSharedMode = _ref$isLoggedOutShare === void 0 ? false : _ref$isLoggedOutShare,
      items = _ref.items,
      labels = _ref.labels,
      _ref$linkComponent = _ref.linkComponent,
      linkComponent = _ref$linkComponent === void 0 ? function (_ref2) {
    var children = _ref2.children;
    return (
      /*#__PURE__*/
      React.createElement("button", null, children)
    );
  } : _ref$linkComponent,
      _ref$modsyButtonConfi = _ref.modsyButtonConfig,
      modsyButtonConfig = _ref$modsyButtonConfi === void 0 ? {
    destination: '/',
    app: 'web-post-purchase'
  } : _ref$modsyButtonConfi,
      onCartClick = _ref.onCartClick,
      _ref$onHelpClick = _ref.onHelpClick,
      onHelpClick = _ref$onHelpClick === void 0 ? function () {} : _ref$onHelpClick,
      _ref$showHelpMenu = _ref.showHelpMenu,
      showHelpMenu = _ref$showHelpMenu === void 0 ? false : _ref$showHelpMenu;

  var _useState = useState(false),
      _useState2 = _slicedToArray(_useState, 2),
      isDrawerOpen = _useState2[0],
      setIsDrawerOpen = _useState2[1];

  var _useState3 = useState(false),
      _useState4 = _slicedToArray(_useState3, 2),
      showDropdown = _useState4[0],
      setShowDropdown = _useState4[1];

  var handleDispatch = function handleDispatch(_ref3) {
    var type = _ref3.type,
        payload = _ref3.payload;
    var isClientSideRoute = get(payload, 'route.app', '') === currentApp;

    switch (true) {
      case type === drawerOverlayClicked:
        setIsDrawerOpen(false);
        break;

      case type === hamburgerToggleClicked:
        setIsDrawerOpen(!isDrawerOpen);
        break;

      case type === navItemWithChevronSmallClicked:
        setShowDropdown(!showDropdown);
        break;

      case type === listItemSublevelClicked && isClientSideRoute:
      case type === secondaryListItemClicked && isClientSideRoute:
        setIsDrawerOpen(false);
        break;
    }

    dispatch({
      type: type,
      payload: payload
    });
  };

  var handleCloseDropdown = function handleCloseDropdown(dropdownLabel) {
    switch (true) {
      case dropdownLabel === labels.myProjects:
        setShowDropdown(false);
        break;
    }
  };

  return (
    /*#__PURE__*/
    React.createElement(Container$6, null, !isDrawerOpen &&
    /*#__PURE__*/
    React.createElement(NavPrimary$2, {
      "data-testid": "nav-menu-primary-group"
    }, backButton.enabled ?
    /*#__PURE__*/
    React.createElement(BackButton$1, {
      "data-testid": 'back-button',
      onClick: backButton.handleClick
    },
    /*#__PURE__*/
    React.createElement(Arrow, {
      ariaLabel: "back",
      direction: "left"
    })) :
    /*#__PURE__*/
    React.createElement(StyledModsyHeart, {
      margin: "0 0.5rem",
      dispatch: dispatch,
      linkComponent: linkComponent,
      modsyButtonConfig: modsyButtonConfig
    }), !backButton.enabled &&
    /*#__PURE__*/
    React.createElement(React.Fragment, null,
    /*#__PURE__*/
    React.createElement(DropdownMenuSmall, {
      dispatch: handleDispatch,
      items: items.myProjects,
      label: labels.myProjects,
      showDropdown: showDropdown,
      handleCloseDropdown: handleCloseDropdown,
      linkComponent: linkComponent
    }),
    /*#__PURE__*/
    React.createElement(NavItemSmall, {
      dispatch: dispatch,
      label: labels.designIdeas,
      navigateTo: get(allRoutes[DESIGN_IDEAS_DRAWER_LABELS.ALL], 'destination', ''),
      smallHeight: true
    }))), React.Children.map(children, function (child) {
      if (child.type.name !== Drawer.name && isDrawerOpen) {
        return null;
      }

      return (
        /*#__PURE__*/
        React.cloneElement(child, {
          dispatch: handleDispatch,
          isDrawerOpen: isDrawerOpen
        })
      );
    }), showHelpMenu && helpModalOpen &&
    /*#__PURE__*/
    React.createElement(StyledNavigationModal$1, _extends({
      dispatch: dispatch,
      linkComponent: linkComponent
    }, items.help)),
    /*#__PURE__*/
    React.createElement(NavSecondary$2, null, !isDrawerOpen && !isLoggedOutSharedMode && showHelpMenu &&
    /*#__PURE__*/
    React.createElement(HelpIcon, {
      onClick: onHelpClick,
      style: {
        marginRight: '1rem'
      }
    }),
    /*#__PURE__*/
    React.createElement(HamburgerToggle, {
      menuId: DRAWER_MENU_ID,
      isOpen: isDrawerOpen,
      onClick: function onClick() {
        handleDispatch({
          type: hamburgerToggleClicked,
          payload: {
            isDrawerOpen: !isDrawerOpen
          }
        });
      }
    })))
  );
}

HeaderSmall.propTypes = {
  /** to replace the Modsy logo with a Back button
   * when the PDP is open */
  backButton: PropTypes.object,

  /** allows rendering of children */
  children: PropTypes.node,

  /** indicates from which app the header originates from */
  currentApp: PropTypes.oneOf(APPS$1),

  /** mechanism for bubbling events up */
  dispatch: PropTypes.func.isRequired,

  /** whether the help modal is open */
  helpModalOpen: PropTypes.bool,

  /** to distinguish logged out users viewing shared studios */
  isLoggedOutSharedMode: PropTypes.bool,

  /** list of items in dropdowns and in cart */
  items: PropTypes.object.isRequired,

  /** user facing copies */
  labels: PropTypes.object.isRequired,

  /** to handle routing */
  linkComponent: PropTypes.func,

  /** to handle routing when click on modsy logo */
  modsyButtonConfig: PropTypes.object,

  /** callback fired after click on cart icon */
  onCartClick: PropTypes.func.isRequired,

  /** callback fired after click on help icon */
  onHelpClick: PropTypes.func,

  /** feature flag to optionally show help menu */
  showHelpMenu: PropTypes.bool
};
var Container$6 = styled.nav(_templateObject$6 || (_templateObject$6 = _taggedTemplateLiteral(["\n  display: flex;\n  justify-content: space-between;\n  align-items: center;\n\n  padding: 1rem;\n  height: 3rem;\n  background-color: ", ";\n  border-bottom: 1px solid ", ";\n"])), surfaceM, museumGrey);
var NavPrimary$2 = styled.div(_templateObject2$6 || (_templateObject2$6 = _taggedTemplateLiteral(["\n  display: flex;\n  flex-direction: row;\n  align-items: center;\n  height: calc(3rem - 1px);\n\n  > a {\n    max-height: calc(3rem - 1px);\n    height: calc(3rem - 1px);\n    display: flex;\n  }\n"])));
var NavSecondary$2 = styled(NavPrimary$2)(_templateObject3$2 || (_templateObject3$2 = _taggedTemplateLiteral(["\n  margin-left: auto;\n"])));
var BackButton$1 = styled.div(_templateObject4$2 || (_templateObject4$2 = _taggedTemplateLiteral(["\n  display: flex;\n  align-items: center;\n\n  cursor: pointer;\n  padding: 1.0625rem 0.5rem;\n"])));
var StyledModsyHeart = styled(ModsyHeart)(_templateObject5$2 || (_templateObject5$2 = _taggedTemplateLiteral(["\n  height: 3rem;\n"])));
var StyledNavigationModal$1 = styled(NavigationModal)(_templateObject6 || (_templateObject6 = _taggedTemplateLiteral(["\n  ", " {\n    display: none\n  }\n"])), MQ_FOR_TABLET_PORTRAIT_UP_NO_MIN_HEIGHT);

var _templateObject$7, _templateObject2$7, _templateObject3$3, _templateObject4$3;

var HeadersLoggedInWithPackage =
/*#__PURE__*/
React.forwardRef(function HeadersLoggedInWithPackage(_ref, ref) {
  var _ref$backButton = _ref.backButton,
      backButton = _ref$backButton === void 0 ? {
    enabled: false,
    text: 'Back',
    handleClick: function handleClick() {}
  } : _ref$backButton,
      _ref$modsyButtonConfi = _ref.modsyButtonConfig,
      modsyButtonConfig = _ref$modsyButtonConfi === void 0 ? {
    destination: '/',
    app: 'web-post-purchase'
  } : _ref$modsyButtonConfi,
      _ref$cartItemCount = _ref.cartItemCount,
      cartItemCount = _ref$cartItemCount === void 0 ? 0 : _ref$cartItemCount,
      _ref$cartState = _ref.cartState,
      cartState = _ref$cartState === void 0 ? {} : _ref$cartState,
      _ref$className = _ref.className,
      className = _ref$className === void 0 ? '' : _ref$className,
      _ref$currentApp = _ref.currentApp,
      currentApp = _ref$currentApp === void 0 ? '' : _ref$currentApp,
      _ref$dataTestId = _ref.dataTestId,
      dataTestId = _ref$dataTestId === void 0 ? '' : _ref$dataTestId,
      _ref$dispatch = _ref.dispatch,
      dispatch = _ref$dispatch === void 0 ? function () {} : _ref$dispatch,
      _ref$headerID = _ref.headerID,
      headerID = _ref$headerID === void 0 ? '' : _ref$headerID,
      _ref$helpModalOpen = _ref.helpModalOpen,
      helpModalOpen = _ref$helpModalOpen === void 0 ? false : _ref$helpModalOpen,
      _ref$isLoggedOutShare = _ref.isLoggedOutSharedMode,
      isLoggedOutSharedMode = _ref$isLoggedOutShare === void 0 ? false : _ref$isLoggedOutShare,
      _ref$isPdpOpen = _ref.isPdpOpen,
      isPdpOpen = _ref$isPdpOpen === void 0 ? false : _ref$isPdpOpen,
      _ref$linkComponent = _ref.linkComponent,
      linkComponent = _ref$linkComponent === void 0 ? function (_ref2) {
    var children = _ref2.children;
    return (
      /*#__PURE__*/
      React.createElement("button", null, children)
    );
  } : _ref$linkComponent,
      _ref$onCartClick = _ref.onCartClick,
      onCartClick = _ref$onCartClick === void 0 ? function () {} : _ref$onCartClick,
      _ref$onHelpClick = _ref.onHelpClick,
      onHelpClick = _ref$onHelpClick === void 0 ? function () {} : _ref$onHelpClick,
      _ref$projects = _ref.projects,
      projects = _ref$projects === void 0 ? [] : _ref$projects,
      _ref$tieredPromoEnabl = _ref.tieredPromoEnabled,
      tieredPromoEnabled = _ref$tieredPromoEnabl === void 0 ? false : _ref$tieredPromoEnabl,
      _ref$topDrawerPositio = _ref.topDrawerPosition,
      topDrawerPosition = _ref$topDrawerPositio === void 0 ? '5.25rem' : _ref$topDrawerPositio,
      _ref$showHelpMenu = _ref.showHelpMenu,
      showHelpMenu = _ref$showHelpMenu === void 0 ? false : _ref$showHelpMenu;
  return (
    /*#__PURE__*/
    React.createElement(StickyHeader, {
      className: className,
      "data-testid": dataTestId,
      id: headerID,
      isPdpOpen: isPdpOpen,
      ref: ref
    },
    /*#__PURE__*/
    React.createElement(HeaderSmallContainer, {
      id: "header-small",
      "data-testid": "header-small"
    },
    /*#__PURE__*/
    React.createElement(HeaderSmall, {
      backButton: backButton,
      currentApp: currentApp,
      dispatch: dispatch,
      helpModalOpen: helpModalOpen,
      items: {
        myProjects: setDropdownTwoLineList({
          projects: projects,
          dispatch: dispatch
        }),
        help: HELP_MODAL_PROPS(),
        inCart: cartItemCount
      },
      labels: {
        myProjects: HEADER_NAMES.PROJECTS,
        designIdeas: HEADER_NAMES.IDEAS
      },
      isLoggedOutSharedMode: isLoggedOutSharedMode,
      itemsInCart: cartItemCount,
      linkComponent: linkComponent,
      modsyButtonConfig: modsyButtonConfig,
      onCartClick: onCartClick,
      onHelpClick: onHelpClick,
      showHelpMenu: showHelpMenu
    },
    /*#__PURE__*/
    React.createElement(Drawer, {
      containerElementId: "header-small",
      dispatch: dispatch,
      isLoggedOutSharedMode: isLoggedOutSharedMode,
      menuId: DRAWER_MENU_ID,
      topPosition: topDrawerPosition
    }, !isLoggedOutSharedMode &&
    /*#__PURE__*/
    React.createElement(Accordion, {
      dispatch: dispatch,
      items: setSublevelTwoLineList({
        projects: projects,
        dispatch: dispatch
      }),
      label: HEADER_NAMES.MY_PROJECTS,
      linkComponent: linkComponent
    }),
    /*#__PURE__*/
    React.createElement(Accordion, {
      dispatch: dispatch,
      items: setSublevelOneLineList(DESIGN_IDEAS_DRAWER_LABELS, dispatch),
      label: HEADER_NAMES.DESIGN_IDEAS,
      linkComponent: linkComponent
    }), isLoggedOutSharedMode ?
    /*#__PURE__*/
    React.createElement(SecondaryList, {
      items: setSecondaryListItemList(LOGGED_OUT_LABELS, dispatch),
      dispatch: dispatch,
      className: "with-padding-bottom"
    }) :
    /*#__PURE__*/
    React.createElement(React.Fragment, null,
    /*#__PURE__*/
    React.createElement(SecondaryList, {
      items: setSecondaryListItemList(ACCOUNT_DRAWER_LABELS, dispatch),
      dispatch: dispatch,
      className: ""
    }))))),
    /*#__PURE__*/
    React.createElement(HeaderMediumContainer, {
      id: "header-medium"
    },
    /*#__PURE__*/
    React.createElement(HeaderMedium, {
      backButton: backButton,
      currentApp: currentApp,
      dispatch: dispatch,
      helpModalOpen: helpModalOpen,
      items: {
        myProjects: setDropdownTwoLineList({
          projects: projects,
          dispatch: dispatch
        }),
        designIdeas: DROPDOWN_DESIGN_IDEAS_LIST(dispatch),
        help: HELP_MODAL_PROPS(),
        inCart: cartItemCount
      },
      labels: {
        myProjects: HEADER_NAMES.MY_PROJECTS,
        designIdeas: HEADER_NAMES.DESIGN_IDEAS
      },
      isLoggedOutSharedMode: isLoggedOutSharedMode,
      linkComponent: linkComponent,
      modsyButtonConfig: modsyButtonConfig,
      onCartClick: onCartClick,
      onHelpClick: onHelpClick,
      showHelpMenu: showHelpMenu
    },
    /*#__PURE__*/
    React.createElement(Drawer, {
      containerElementId: "header-medium",
      dispatch: dispatch,
      isLoggedOutSharedMode: isLoggedOutSharedMode,
      menuId: DRAWER_MENU_ID,
      topPosition: topDrawerPosition
    }, !isLoggedOutSharedMode &&
    /*#__PURE__*/
    React.createElement(Accordion, {
      dispatch: dispatch,
      items: setSublevelTwoLineList({
        projects: projects,
        dispatch: dispatch
      }),
      label: HEADER_NAMES.MY_PROJECTS,
      linkComponent: linkComponent
    }),
    /*#__PURE__*/
    React.createElement(Accordion, {
      dispatch: dispatch,
      items: setSublevelOneLineList(DESIGN_IDEAS_DRAWER_LABELS, dispatch),
      label: HEADER_NAMES.DESIGN_IDEAS,
      linkComponent: linkComponent
    }), isLoggedOutSharedMode ?
    /*#__PURE__*/
    React.createElement(SecondaryList, {
      items: setSecondaryListItemList(LOGGED_OUT_LABELS, dispatch),
      dispatch: dispatch,
      className: "with-padding-bottom"
    }) :
    /*#__PURE__*/
    React.createElement(React.Fragment, null,
    /*#__PURE__*/
    React.createElement(SecondaryList, {
      items: setSecondaryListItemList(ACCOUNT_DRAWER_LABELS, dispatch),
      dispatch: dispatch,
      className: ""
    }))))),
    /*#__PURE__*/
    React.createElement(HeaderLargeContainer, {
      "data-testid": "header-large"
    },
    /*#__PURE__*/
    React.createElement(HeaderLarge, {
      cartState: cartState,
      dispatch: dispatch,
      icons: {
        account: showHelpMenu ?
        /*#__PURE__*/
        React.createElement(AccountIcon, null) : undefined,
        help: showHelpMenu ?
        /*#__PURE__*/
        React.createElement(HelpIcon, null) : undefined
      },
      items: {
        myProjects: setDropdownTwoLineList({
          projects: projects,
          dispatch: dispatch
        }),
        designIdeas: DROPDOWN_DESIGN_IDEAS_LIST(dispatch),
        account: DROPDOWN_ACCOUNT_LIST_WITH_HELP(dispatch),
        help: DROPDOWN_HELP_LIST(dispatch)
      },
      labels: {
        myProjects: HEADER_NAMES.MY_PROJECTS,
        designIdeas: HEADER_NAMES.DESIGN_IDEAS,
        account: HEADER_NAMES.ACCOUNT,
        login: HEADER_NAMES.LOGIN,
        help: HEADER_NAMES.HELP
      },
      isLoggedOutSharedMode: isLoggedOutSharedMode,
      itemsInCart: cartItemCount,
      linkComponent: linkComponent,
      modsyButtonConfig: modsyButtonConfig,
      onCartClick: onCartClick,
      tieredPromoEnabled: tieredPromoEnabled,
      showHelpMenu: showHelpMenu
    })))
  );
}); // Styles

var StickyHeader = styled.header(_templateObject$7 || (_templateObject$7 = _taggedTemplateLiteral(["\n  position: fixed; /* added to support older browsers */\n  position: sticky;\n  top: 0;\n"])));
var HeaderSmallContainer = styled.div(_templateObject2$7 || (_templateObject2$7 = _taggedTemplateLiteral(["\n  width: 100vw;\n\n  ", " {\n    display: none;\n  }\n"])), MQ_FOR_TABLET_PORTRAIT_UP_NO_MIN_HEIGHT);
var HeaderMediumContainer = styled.div(_templateObject3$3 || (_templateObject3$3 = _taggedTemplateLiteral(["\n  ", " {\n    width: 100vw;\n  }\n\n  ", " {\n    display: none\n  }\n\n  ", " {\n    display: none\n  }\n"])), MQ_FOR_SMALL_TABLET_ONLY, MQ_FOR_PHONE_WIDTH, MQ_FOR_TABLET_LANDSCAPE_UP);
var HeaderLargeContainer = styled.div(_templateObject4$3 || (_templateObject4$3 = _taggedTemplateLiteral(["\n  ", " {\n    display: none\n  }\n"])), MQ_FOR_TABLET_LANDSCAPE_BELOW);

var _templateObject$8, _templateObject2$8, _templateObject3$4, _templateObject4$4, _templateObject5$3;

function HeaderLoggedOutLarge(_ref) {
  var _ref$className = _ref.className,
      className = _ref$className === void 0 ? '' : _ref$className,
      _ref$dispatch = _ref.dispatch,
      dispatch = _ref$dispatch === void 0 ? function () {} : _ref$dispatch,
      _ref$items = _ref.items,
      items = _ref$items === void 0 ? {} : _ref$items,
      _ref$labels = _ref.labels,
      labels = _ref$labels === void 0 ? {} : _ref$labels,
      _ref$linkComponent = _ref.linkComponent,
      linkComponent = _ref$linkComponent === void 0 ? function (_ref2) {
    var children = _ref2.children;
    return (
      /*#__PURE__*/
      React.createElement("button", null, children)
    );
  } : _ref$linkComponent,
      _ref$modsyButtonConfi = _ref.modsyButtonConfig,
      modsyButtonConfig = _ref$modsyButtonConfi === void 0 ? {
    destination: '/',
    app: 'web-post-purchase'
  } : _ref$modsyButtonConfi;
  return (
    /*#__PURE__*/
    React.createElement(Container$7, null,
    /*#__PURE__*/
    React.createElement(NavPrimary$3, null,
    /*#__PURE__*/
    React.createElement(StyledModsyLogo$1, {
      dispatch: dispatch,
      linkComponent: linkComponent,
      modsyButtonConfig: modsyButtonConfig
    }),
    /*#__PURE__*/
    React.createElement(VerticalLine$1, {
      role: "img",
      "aria-label": "separator"
    }),
    /*#__PURE__*/
    React.createElement(NavItemLarge, {
      dispatch: dispatch,
      label: labels.styleQuiz,
      navigateTo: get(allRoutes[HEADER_NAMES.STYLE_QUIZ], 'destination', '')
    }),
    /*#__PURE__*/
    React.createElement(DropdownMenuLarge, {
      className: className,
      dispatch: dispatch,
      label: labels.designIdeas,
      items: items.designIdeas,
      linkComponent: linkComponent
    })),
    /*#__PURE__*/
    React.createElement(NavSecondary$3, null,
    /*#__PURE__*/
    React.createElement(NavItemLarge, {
      dispatch: dispatch,
      label: labels.login,
      navigateTo: webPP.login()
    })))
  );
}

HeaderLoggedOutLarge.propTypes = {
  /** to allow extended styling */
  className: PropTypes.string,

  /** mechanism for bubbling events up */
  dispatch: PropTypes.func.isRequired,

  /** list of items */
  items: PropTypes.object.isRequired,

  /** user facing copies */
  labels: PropTypes.object.isRequired,

  /** to handle routing */
  linkComponent: PropTypes.func,

  /** to handle routing when click on modsy logo */
  modsyButtonConfig: PropTypes.object
};
var Container$7 = styled.nav(_templateObject$8 || (_templateObject$8 = _taggedTemplateLiteral(["\n  display: flex;\n  justify-content: space-between;\n  align-items: center;\n\n  padding: 1.5rem;\n  height: 4rem;\n  background-color: ", ";\n  border-bottom: 1px solid ", ";\n"])), surfaceM, museumGrey);
var VerticalLine$1 = styled.span(_templateObject2$8 || (_templateObject2$8 = _taggedTemplateLiteral(["\n  border-right: 0.0625rem solid ", ";\n  height: 1.5rem;\n  margin: 0 0.75rem 0 1.5rem;\n"])), grayLightM);
var NavPrimary$3 = styled.div(_templateObject3$4 || (_templateObject3$4 = _taggedTemplateLiteral(["\n  display: flex;\n  flex-direction: row;\n  align-items: center;\n"])));
var NavSecondary$3 = styled(NavPrimary$3)(_templateObject4$4 || (_templateObject4$4 = _taggedTemplateLiteral([""])));
var StyledModsyLogo$1 = styled(ModsyLogo)(_templateObject5$3 || (_templateObject5$3 = _taggedTemplateLiteral(["\n  height: 3.75rem;\n"])));

var _templateObject$9, _templateObject2$9, _templateObject3$5;

var APPS$2 = ['', 'nextjs', 'web-post-purchase', 'wordpress'];

function HeaderLoggedOutSmall(_ref) {
  var _ref$children = _ref.children,
      children = _ref$children === void 0 ? [] : _ref$children,
      _ref$currentApp = _ref.currentApp,
      currentApp = _ref$currentApp === void 0 ? '' : _ref$currentApp,
      _ref$dispatch = _ref.dispatch,
      dispatch = _ref$dispatch === void 0 ? function () {} : _ref$dispatch,
      _ref$linkComponent = _ref.linkComponent,
      linkComponent = _ref$linkComponent === void 0 ? function (_ref2) {
    var children = _ref2.children;
    return (
      /*#__PURE__*/
      React.createElement("button", null, children)
    );
  } : _ref$linkComponent,
      _ref$modsyButtonConfi = _ref.modsyButtonConfig,
      modsyButtonConfig = _ref$modsyButtonConfi === void 0 ? {
    destination: '/',
    app: 'web-post-purchase'
  } : _ref$modsyButtonConfi;

  var _useState = useState(false),
      _useState2 = _slicedToArray(_useState, 2),
      isDrawerOpen = _useState2[0],
      setIsDrawerOpen = _useState2[1];

  var _useState3 = useState(false),
      _useState4 = _slicedToArray(_useState3, 2),
      showDropdown = _useState4[0],
      setShowDropdown = _useState4[1];

  var handleDispatch = function handleDispatch(_ref3) {
    var type = _ref3.type,
        payload = _ref3.payload;
    var isClientSideRoute = get(payload, 'route.app', '') === currentApp;

    switch (true) {
      case type === drawerOverlayClicked:
        setIsDrawerOpen(false);
        break;

      case type === hamburgerToggleClicked:
        setIsDrawerOpen(!isDrawerOpen);
        break;

      case type === navItemWithChevronSmallClicked:
        setShowDropdown(!showDropdown);
        break;

      case type === listItemSublevelClicked && isClientSideRoute:
      case type === secondaryListItemClicked && isClientSideRoute:
        setIsDrawerOpen(false);
        break;
    }

    dispatch({
      type: type,
      payload: payload
    });
  };

  return (
    /*#__PURE__*/
    React.createElement(Container$8, null, !isDrawerOpen &&
    /*#__PURE__*/
    React.createElement(NavPrimary$4, null,
    /*#__PURE__*/
    React.createElement(ModsyLogo, {
      margin: "0 0.5rem 0 0",
      variant: "small",
      dispatch: dispatch,
      linkComponent: linkComponent,
      modsyButtonConfig: modsyButtonConfig
    })), React.Children.map(children, function (child) {
      if (child.type.name !== Drawer.name && isDrawerOpen) {
        return null;
      }

      return (
        /*#__PURE__*/
        React.cloneElement(child, {
          dispatch: handleDispatch,
          isDrawerOpen: isDrawerOpen
        })
      );
    }),
    /*#__PURE__*/
    React.createElement(NavSecondary$4, null,
    /*#__PURE__*/
    React.createElement(HamburgerToggle, {
      menuId: DRAWER_MENU_ID,
      isOpen: isDrawerOpen,
      onClick: function onClick() {
        handleDispatch({
          type: hamburgerToggleClicked,
          payload: {
            isDrawerOpen: !isDrawerOpen
          }
        });
      }
    })))
  );
}

HeaderLoggedOutSmall.propTypes = {
  /** allows rendering of children */
  children: PropTypes.node,

  /** indicates from which app the header originates from */
  currentApp: PropTypes.oneOf(APPS$2),

  /** mechanism for bubbling events up */
  dispatch: PropTypes.func.isRequired,

  /** to handle routing */
  linkComponent: PropTypes.func,

  /** to handle routing when click on modsy logo */
  modsyButtonConfig: PropTypes.object
};
var Container$8 = styled.nav(_templateObject$9 || (_templateObject$9 = _taggedTemplateLiteral(["\n  display: flex;\n  justify-content: space-between;\n  align-items: center;\n\n  padding: 1rem;\n  height: 3rem;\n  background-color: ", ";\n  border-bottom: 1px solid ", ";\n"])), surfaceM, museumGrey);
var NavPrimary$4 = styled.div(_templateObject2$9 || (_templateObject2$9 = _taggedTemplateLiteral(["\n  display: flex;\n  flex-direction: row;\n  align-items: center;\n"])));
var NavSecondary$4 = styled(NavPrimary$4)(_templateObject3$5 || (_templateObject3$5 = _taggedTemplateLiteral(["\n  margin-left: auto;\n  gap: 10px;\n"])));

var _templateObject$a, _templateObject2$a, _templateObject3$6;

var HeadersLoggedOut =
/*#__PURE__*/
React.forwardRef(function HeadersLoggedOut(_ref, ref) {
  var _ref$modsyButtonConfi = _ref.modsyButtonConfig,
      modsyButtonConfig = _ref$modsyButtonConfi === void 0 ? {
    destination: '/',
    app: 'web-post-purchase'
  } : _ref$modsyButtonConfi,
      _ref$className = _ref.className,
      className = _ref$className === void 0 ? '' : _ref$className,
      _ref$currentApp = _ref.currentApp,
      currentApp = _ref$currentApp === void 0 ? '' : _ref$currentApp,
      _ref$dataTestId = _ref.dataTestId,
      dataTestId = _ref$dataTestId === void 0 ? '' : _ref$dataTestId,
      _ref$dispatch = _ref.dispatch,
      dispatch = _ref$dispatch === void 0 ? function () {} : _ref$dispatch,
      _ref$headerID = _ref.headerID,
      headerID = _ref$headerID === void 0 ? '' : _ref$headerID,
      _ref$linkComponent = _ref.linkComponent,
      linkComponent = _ref$linkComponent === void 0 ? function (_ref2) {
    var children = _ref2.children;
    return (
      /*#__PURE__*/
      React.createElement("button", null, children)
    );
  } : _ref$linkComponent,
      _ref$topDrawerPositio = _ref.topDrawerPosition,
      topDrawerPosition = _ref$topDrawerPositio === void 0 ? '3rem' : _ref$topDrawerPositio;
  return (
    /*#__PURE__*/
    React.createElement(StickyHeader$1, {
      className: className,
      "data-testid": dataTestId,
      id: headerID,
      ref: ref
    },
    /*#__PURE__*/
    React.createElement(HeaderSmallContainer$1, {
      id: "header-small",
      "data-testid": "header-small"
    },
    /*#__PURE__*/
    React.createElement(HeaderLoggedOutSmall, {
      currentApp: currentApp,
      dispatch: dispatch,
      linkComponent: linkComponent,
      modsyButtonConfig: modsyButtonConfig
    },
    /*#__PURE__*/
    React.createElement(Drawer, {
      containerElementId: "header-small",
      dispatch: dispatch,
      menuId: DRAWER_MENU_ID,
      topPosition: topDrawerPosition
    },
    /*#__PURE__*/
    React.createElement(NavItemSmall, {
      dispatch: dispatch,
      label: HEADER_NAMES.LOGIN,
      navigateTo: webPP.login()
    }),
    /*#__PURE__*/
    React.createElement(Accordion, {
      dispatch: dispatch,
      items: setSublevelOneLineList(DESIGN_IDEAS_DRAWER_LABELS, dispatch),
      label: HEADER_NAMES.DESIGN_IDEAS,
      linkComponent: linkComponent
    })))),
    /*#__PURE__*/
    React.createElement(HeaderLargeContainer$1, null,
    /*#__PURE__*/
    React.createElement(HeaderLoggedOutLarge, {
      dispatch: dispatch,
      items: {
        howItWorks: DROPDOWN_HOW_IT_WORKS_LIST(dispatch),
        designIdeas: DROPDOWN_DESIGN_IDEAS_LIST(dispatch)
      },
      labels: {
        howItWorks: HEADER_NAMES.HOW_IT_WORKS,
        examples: HEADER_NAMES.EXAMPLES,
        // DISABLED AS PART OF MODSY WIND DOWN WORK
        // styleQuiz: HEADER_NAMES.STYLE_QUIZ,
        designIdeas: HEADER_NAMES.DESIGN_IDEAS,
        login: HEADER_NAMES.LOGIN
      },
      linkComponent: linkComponent,
      modsyButtonConfig: modsyButtonConfig
    })))
  );
}); // Styles

var StickyHeader$1 = styled.header(_templateObject$a || (_templateObject$a = _taggedTemplateLiteral(["\n  position: fixed!important;\n  top: auto;\n  width: 100%;\n"])));
var HeaderSmallContainer$1 = styled.div(_templateObject2$a || (_templateObject2$a = _taggedTemplateLiteral(["\n  width: 100vw;\n\n  ", " {\n    display: none;\n  }\n"])), MQ_FOR_TABLET_LANDSCAPE_UP);
var HeaderLargeContainer$1 = styled.div(_templateObject3$6 || (_templateObject3$6 = _taggedTemplateLiteral(["\n  ", " {\n    display: none\n  }\n"])), MQ_FOR_TABLET_LANDSCAPE_BELOW);
export { Drawer, HeaderLarge, HeaderMedium, HeaderSmall, HeadersLoggedInWithPackage, HeadersLoggedOut };